import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from "@/components/ui/table";
import {
  ArrowLeft,
  Download,
  RefreshCw,
  Users,
  LogIn,
  LogOut,
  Trash2
} from "lucide-react";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `/api`;

export default function Admin() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({ total: 0, checked_in: 0, checked_out: 0 });
  const [attendees, setAttendees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [eventType, setEventType] = useState("Learning Mahjong");

  const fetchData = useCallback(async (silent = false, specificEvent = eventType) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const [statsRes, attendeesRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { params: { event_type: specificEvent } }),
        axios.get(`${API}/admin/attendees`, { params: { event_type: specificEvent } }),
      ]);
      setStats(statsRes.data);
      setAttendees(attendeesRes.data);
    } catch (err) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [eventType]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(() => fetchData(true), 15000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleExportCSV = () => {
    window.open(`${API}/admin/export-csv?event_type=${encodeURIComponent(eventType)}`, "_blank");
  };

  const handleClearAll = async () => {
    if (!window.confirm("WARNING: This will permanently delete ALL attendee records from the database and the Google Sheet. This action cannot be undone. Are you sure?")) {
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/admin/clear-all`);
      toast.success("All data has been cleared successfully");
      fetchData();
    } catch (err) {
      toast.error("Failed to clear data");
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (iso) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      day: "2-digit",
      month: "short",
    });
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Header */}
      <div className="glass-header sticky top-0 z-50 px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/")}
              className="flex items-center gap-2 text-sm text-[#525252] hover:text-[#0A0A0A] transition-colors"
              data-testid="admin-back-btn"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div>
              <div className="mb-1">
                <img
                  src="/EO Odisha Logo.png"
                  alt="EO Odisha Logo"
                  className="h-8 w-auto object-contain"
                />
                <p className="text-[10px] text-[#525252] uppercase tracking-widest font-semibold mt-0.5">Admin Dashboard</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="mr-4 w-48">
              <Select
                value={eventType}
                onValueChange={(val) => {
                  setEventType(val);
                  fetchData(false, val);
                }}
              >
                <SelectTrigger className="h-9 rounded-none border-black/15 text-xs">
                  <SelectValue placeholder="Select Event" />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="Learning Mahjong">Learning Mahjong</SelectItem>
                  <SelectItem value="TransformatiOWN">TransformatiOWN</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchData(true)}
              disabled={refreshing}
              className="rounded-none border-black/15 text-xs uppercase tracking-wider"
              data-testid="admin-refresh-btn"
            >
              <RefreshCw className={`w-3 h-3 ${refreshing ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button
              size="sm"
              onClick={handleExportCSV}
              className="btn-brand rounded-none text-xs"
              data-testid="admin-csv-export"
            >
              <Download className="w-3 h-3" />
              Export CSV
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={handleClearAll}
              disabled={loading || refreshing}
              className="rounded-none text-xs uppercase tracking-wider hidden md:flex gap-2"
              data-testid="admin-clear-all"
            >
              <Trash2 className="w-3 h-3" />
              Clear All
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-3 gap-0 border border-black/10 mb-8 animate-fade-in">
          <div className="stat-card p-6 border-r border-black/10" data-testid="stat-total">
            <div className="flex items-center gap-2 mb-3">
              <Users className="w-4 h-4 text-[#525252]" />
              <p className="overline text-[#525252]">Total</p>
            </div>
            <p className="text-3xl sm:text-4xl font-black font-mono text-[#0A0A0A] tracking-tighter">
              {stats.total}
            </p>
          </div>
          <div className="stat-card p-6 border-r border-black/10" data-testid="stat-checked-in">
            <div className="flex items-center gap-2 mb-3">
              <LogIn className="w-4 h-4 text-[#8B1E2D]" />
              <p className="overline text-[#525252]">Checked In</p>
            </div>
            <p className="text-3xl sm:text-4xl font-black font-mono text-[#8B1E2D] tracking-tighter">
              {stats.checked_in}
            </p>
          </div>
          <div className="stat-card p-6" data-testid="stat-checked-out">
            <div className="flex items-center gap-2 mb-3">
              <LogOut className="w-4 h-4 text-[#525252]" />
              <p className="overline text-[#525252]">Checked Out</p>
            </div>
            <p className="text-3xl sm:text-4xl font-black font-mono text-[#0A0A0A] tracking-tighter">
              {stats.checked_out}
            </p>
          </div>
        </div>

        {/* Attendees Table */}
        <div className="animate-fade-in animate-delay-2">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-[#0A0A0A] tracking-tight">Attendees</h2>
            <p className="text-xs text-[#525252] font-mono">{attendees.length} records</p>
          </div>

          {loading ? (
            <div className="border border-black/10 bg-white p-12 text-center">
              <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-3 text-[#525252]" />
              <p className="text-sm text-[#525252]">Loading attendees...</p>
            </div>
          ) : attendees.length === 0 ? (
            <div className="border border-black/10 bg-white p-12 text-center" data-testid="empty-state">
              <Users className="w-8 h-8 mx-auto mb-3 text-[#525252]/40" />
              <p className="text-sm text-[#525252]">No attendees yet</p>
            </div>
          ) : (
            <div className="border border-black/10 bg-white overflow-hidden" data-testid="attendees-table">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#FAFAFA] border-b border-black/10">
                      <TableHead className="font-mono text-xs uppercase tracking-wider text-[#525252] font-semibold">
                        Event ID
                      </TableHead>
                      <TableHead className="font-mono text-xs uppercase tracking-wider text-[#525252] font-semibold">
                        Name
                      </TableHead>
                      <TableHead className="font-mono text-xs uppercase tracking-wider text-[#525252] font-semibold hidden sm:table-cell">
                        Contact Number
                      </TableHead>
                      <TableHead className="font-mono text-xs uppercase tracking-wider text-[#525252] font-semibold">
                        Membership Type
                      </TableHead>
                      <TableHead className="font-mono text-xs uppercase tracking-wider text-[#525252] font-semibold">
                        Entry
                      </TableHead>
                      <TableHead className="font-mono text-xs uppercase tracking-wider text-[#525252] font-semibold">
                        Exit
                      </TableHead>
                      <TableHead className="font-mono text-xs uppercase tracking-wider text-[#525252] font-semibold">
                        Status
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {attendees.map((a) => (
                      <TableRow key={a.id} className="border-b border-black/5" data-testid={`attendee-row-${a.event_id}`}>
                        <TableCell className="font-mono text-sm font-semibold text-[#0A0A0A]">
                          {a.event_id}
                        </TableCell>
                        <TableCell className="text-sm font-medium text-[#0A0A0A]">
                          {a.name}
                        </TableCell>
                        <TableCell className="text-sm text-[#525252] hidden sm:table-cell">
                          {a.contact}
                        </TableCell>
                        <TableCell className="text-sm font-medium text-[#0A0A0A]">
                          {a.type || "—"}
                        </TableCell>
                        <TableCell className="text-xs font-mono text-[#525252]">
                          {formatTime(a.entry_time)}
                        </TableCell>
                        <TableCell className="text-xs font-mono text-[#525252]">
                          {formatTime(a.exit_time)}
                        </TableCell>
                        <TableCell>
                          {a.exit_time ? (
                            <Badge
                              variant="outline"
                              className="rounded-none text-xs font-mono border-black/20 text-[#525252]"
                              data-testid={`status-exited-${a.event_id}`}
                            >
                              Exited
                            </Badge>
                          ) : (
                            <Badge
                              className="rounded-none text-xs font-mono bg-[#002FA7] text-white border-[#002FA7]"
                              data-testid={`status-active-${a.event_id}`}
                            >
                              Active
                            </Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
